import { Fragment, useEffect, useMemo, useRef, useState } from 'react'

import { Breadcrumb } from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import flatten from 'lodash/flatten'
import { useTranslation } from 'react-i18next'
import { useLocation, useMatches } from 'react-router'
import { useHydrated } from 'remix-utils/use-hydrated'
import { useMediaQuery, useOnClickOutside } from 'usehooks-ts'

import { BreadcrumbEllipsis } from './BreadcrumbEllipsis'
import { BreadcrumbItem } from './BreadcrumbItem'
import { useBreadcrumbsVisibility } from '~/portal/hooks/useBreadcrumbsVisibility'
import appDefaults from '~/portal/portal.defaults.json'
import { PortalRouteHandle, PortalRouteMatch } from '~/portal/types/app'
import { sanitizeId } from '~/portal/utils/shared/content'

const hasBreadcrumb = (match: Partial<PortalRouteMatch>): match is PortalRouteMatch & { handle: PortalRouteHandle } =>
  !!match.handle && typeof match.handle.breadcrumb === 'function' ||
  !!match.handle && typeof match.handle.nestedBreadcrumbs === 'function'

export const BreadcrumbBar = () => {

  const matches: PortalRouteMatch[] = useMatches()

  const containerRef = useRef(null) as unknown as React.RefObject<HTMLDivElement>

  const location = useLocation()

  const [isExpanded, setIsExpanded] = useState(false)

  const { t } = useTranslation()

  const breadcrumbs = matches.filter(hasBreadcrumb)

  const isDesktop = useMediaQuery(appDefaults.media.desktop, { initializeWithValue: false, defaultValue: true })
  const isTabletAndDesktop = useMediaQuery(appDefaults.media.tabletAndDesktop, { initializeWithValue: false, defaultValue: true })

  useOnClickOutside(containerRef, () => {
    if (!isDesktop) {
      setIsExpanded(false)
    }
  })

  const breadcrumbsData = useMemo(() =>
    flatten(breadcrumbs.map((match) => {
      if (match.handle?.nestedBreadcrumbs) {
        return match.handle.nestedBreadcrumbs({ match, matches })
      }
      return ({
        title: match.handle.breadcrumb?.({ match, t }) || '',
        path: match.pathname,
        id: sanitizeId(`breadcrumb-${match.pathname}`)
      })
    })), [location.pathname])

  const { showEllipsis, visibilityData, firstAndLastExceedSpace } = useBreadcrumbsVisibility(breadcrumbsData, containerRef)

  const renderTwoLines = !isTabletAndDesktop && firstAndLastExceedSpace

  const getWrappedHeight = () => renderTwoLines ? 68 : 38

  const isHydrated = useHydrated()

  const [height, setHeight] = useState<number>(getWrappedHeight())

  useEffect(() => {
    setHeight(getWrappedHeight())
  }, [renderTwoLines])

  useEffect(() => {
    if (containerRef.current) {
      const updateHeight = () => {
        if (isExpanded) {
          const scrollHeight = (containerRef.current?.firstChild as HTMLElement)?.getBoundingClientRect()?.height
          setHeight(scrollHeight || 0)
        } else {
          setHeight(getWrappedHeight())
        }
      }

      updateHeight()
    }
  }, [isExpanded, breadcrumbsData])

  if (!isHydrated || breadcrumbsData.length <= 1) {
    return null
  }

  return (
    <>
      <div ref={containerRef}
        style={{ height: `${height}px` }}
        className={clsx(
          'sp-content-breadcrumb sm:fixed left-0 block w-full max-w-full px-2.5 border-b border-t border-(--sp-breadcrumb-border-color,var(--neutral-30)) dark:border-(--sp-breadcrumb-border-color,var(--neutral-80)) bg-(--sp-color-surface) z-38',
          'xl:relative xl:p-0 xl:border-none',
          'overflow-hidden'
        )}
        data-testid="breadcrumb-wrapper"
      >
        <div className="flex py-1 xl:py-0 whitespace-nowrap"
        >
          <Breadcrumb
            wrapBreadcrumbItems={isExpanded || renderTwoLines}
            className={clsx((isExpanded || (!isExpanded && firstAndLastExceedSpace)) && 'w-full')}
          >
            {breadcrumbsData.map((item, i) => (
              <Fragment key={item.id}>
                {i === 1 && showEllipsis && !isExpanded ?
                  <BreadcrumbEllipsis
                    onClick={() => setIsExpanded(true)}
                  />
                  : null as any}
                <BreadcrumbItem
                  id={item.id}
                  href={item.path}
                  isActive={i === breadcrumbsData.length - 1}
                  isConsumerSize={true}
                  className={clsx(visibilityData.find(el => el.id === item.id)?.hidden && !isExpanded && 'hidden')}
                  textEllipsis={isExpanded || !isExpanded && firstAndLastExceedSpace && i === breadcrumbsData.length - 1}
                >
                  {item.title}
                </BreadcrumbItem>
              </Fragment>
            ))}

          </Breadcrumb>
        </div>
      </div>
      <div
        style={{ height }}
        className="transition-all duration-300 ease-in xl:hidden"></div>
    </>
  )
}